import * as React from 'react';
import { SelectBase } from './SelectBase';
import { useSelectTags } from './hooks/useSelectTags';
import type {
	IMultiSelectProps,
	IOption,
	ISelectChangeMeta,
	ISelectValueRenderState,
	SelectOptionValue,
} from './types';
import { Button } from '../button/button';
import { Tags } from '../tags/tags';
import { CaptionTypography, Close, Tag, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/atoms';
import type { TagProps } from '@components/atoms/tag/types';
import { cn } from '@utils';

const OVERFLOW_TAG_RESERVED_WIDTH = 32;
const OVERFLOW_TOOLTIP_DELAY_MS = 100;

export const MultiSelect = <TValue extends SelectOptionValue = SelectOptionValue, TMetadata = unknown>({
	className,
	disabled = false,
	enableSelectAll = false,
	getOverflowAriaLabel,
	getTagRemoveAriaLabel,
	maxSelections,
	maxSelectionsLabel,
	onChange,
	options,
	renderValue,
	selectAllLabel,
	value,
	...props
}: IMultiSelectProps<TValue, TMetadata>): React.ReactElement => {
	const { canSelectMore, hasReachedMaxSelections, removeSelectedValue, selectedOptions } = useSelectTags({
		maxSelections,
		onChange,
		options,
		value,
	});

	const selectableValues = React.useMemo(
		() => options.filter(option => !option.disabled).map(option => option.value),
		[options]
	);

	const allSelectableValuesSelected =
		selectableValues.length > 0 && selectableValues.every(selectableValue => value.includes(selectableValue));

	const optionsWithSelectionLimit = React.useMemo(
		() => {
			if (canSelectMore) {
				return options;
			}

			return options.map(option => ({
				...option,
				disabled: option.disabled || !value.includes(option.value),
			}));
		},
		[canSelectMore, options, value]
	);

	const handleChange = React.useCallback(
		(nextValue: ReadonlyArray<TValue>, meta: ISelectChangeMeta<TValue, TMetadata>) => {
			if (meta.action === 'select' && !canSelectMore && nextValue.length > value.length) {
				return;
			}

			if (meta.action === 'select' && maxSelections !== undefined && nextValue.length > maxSelections) {
				onChange(nextValue.slice(0, maxSelections));
				return;
			}

			onChange(nextValue);
		},
		[canSelectMore, maxSelections, onChange, value.length]
	);

	const handleSelectAll = React.useCallback(() => {
		if (disabled || allSelectableValuesSelected) {
			return;
		}

		const selectedValues = new Set(value);
		const unselectedValues = selectableValues.filter(selectableValue => !selectedValues.has(selectableValue));
		const availableSelectionCount = maxSelections === undefined ? unselectedValues.length : maxSelections - value.length;

		if (availableSelectionCount <= 0) {
			return;
		}

		onChange([...value, ...unselectedValues.slice(0, availableSelectionCount)]);
	}, [allSelectableValuesSelected, disabled, maxSelections, onChange, selectableValues, value]);

	const renderRemoveButton = React.useCallback(
		(option: IOption<TValue, TMetadata>): React.ReactNode => (
			<Button
				aria-label={getTagRemoveAriaLabel(option)}
				className="size-3 rounded-full focus-visible:ring-2 focus-visible:ring-highlightSelected"
				data-testid="multi-select-tag-remove"
				iconButton
				onClick={() => removeSelectedValue(option.value)}
				size="xs"
				type="button"
				variant="icon"
			>
				<Close aria-hidden="true" className="h-2.5 w-2.5" />
			</Button>
		),
		[getTagRemoveAriaLabel, removeSelectedValue]
	);

	const tagItems = React.useMemo<ReadonlyArray<TagProps>>(
		() =>
			selectedOptions.map(option => ({
				children: option.label,
				color: 'gray',
				rightIcon: disabled ? null : renderRemoveButton(option),
				type: 'basic',
			})),
		[disabled, renderRemoveButton, selectedOptions]
	);

	const shouldShowSelectAll = enableSelectAll && selectableValues.length > 0;
	const selectAllDisabled = disabled || !canSelectMore || allSelectableValuesSelected;
	const renderTriggerValue = React.useCallback(
		(options: ReadonlyArray<IOption<TValue, TMetadata>>, state: ISelectValueRenderState): React.ReactNode =>
			renderValue ? (
				renderValue(options, state)
			) : (
				<CaptionTypography className="text-secondary">{state.placeholder}</CaptionTypography>
			),
		[renderValue]
	);
	const renderOverflowTag = React.useCallback(
		(items: ReadonlyArray<TagProps>): React.ReactNode => {
			const overflowStartIndex = tagItems.length - items.length;

			return (
				<TooltipProvider delayDuration={OVERFLOW_TOOLTIP_DELAY_MS}>
					<Tooltip>
						<TooltipTrigger asChild>
							<Tag
								aria-label={getOverflowAriaLabel(items.length)}
								className="cursor-default focus:outline-hidden focus-visible:ring-2 focus-visible:ring-highlightSelected"
								color="gray"
								data-testid="multi-select-overflow-tag"
								tabIndex={0}
								type="basic"
							>
								+{items.length}
							</Tag>
						</TooltipTrigger>
						<TooltipContent>
							<div className="flex flex-col gap-1" data-testid="multi-select-overflow-tooltip">
								{items.map((item, index) => {
									const option = selectedOptions[overflowStartIndex + index];

									if (!option) {
										return null;
									}

									return (
										<Tag
											key={String(option.value)}
											color="gray"
											rightIcon={disabled ? null : renderRemoveButton(option)}
											type="basic"
										>
											{item.children}
										</Tag>
									);
								})}
							</div>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			);
		},
		[disabled, getOverflowAriaLabel, renderRemoveButton, selectedOptions, tagItems.length]
	);

	return (
		<div className={cn('w-full space-y-2', className)} data-testid="multi-select">
			{selectedOptions.length > 0 && (
				<Tags
					items={tagItems}
					overflowIndicatorWidth={OVERFLOW_TAG_RESERVED_WIDTH}
					renderOverflow={renderOverflowTag}
					truncate
				/>
			)}
			{shouldShowSelectAll && (
				<Button
					className="h-fit text-sm font-semibold text-primary underline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
					data-testid="multi-select-select-all"
					disabled={selectAllDisabled}
					onClick={handleSelectAll}
					size="xs"
					type="button"
					variant="hyperlink"
				>
					{selectAllLabel}
				</Button>
			)}
			<SelectBase
				{...props}
				disabled={disabled}
				onChange={handleChange}
				options={optionsWithSelectionLimit}
				renderValue={renderTriggerValue}
				selectionMode="multiple"
				value={value}
			/>
			{hasReachedMaxSelections && maxSelectionsLabel && (
				<div data-testid="multi-select-max-selections" role="status">
					<CaptionTypography className="text-secondary">{maxSelectionsLabel}</CaptionTypography>
				</div>
			)}
		</div>
	);
};
